import { observer } from "mobx-react-lite";
import bodyStylesManager from "../../../stores/body-styles-manager";
import contentManager from "../../../stores/content-manager";
import SatoriDimensionsBox from "../shared/satori-ui/dimensions-box";
import SatoriDropDownList from "../shared/satori-ui/dropdownlist";
import { IHeadingBlockData } from "../../../types/content-block-data";
import type {TextTransformType, ITypographyRole} from "../../../stores/state-interfaces";
import { cleanDimenValues } from "../../../lib/page-builder-lib";
import glideBarManager from "../../../stores/glidebar-manager";
import { 
  STANDARD_FONT_FAMILIES, 
  ALIGN_OPTIONS, 
  FONT_WEIGHTS, 
  TEXT_DECORATIONS, 
  TEXT_TRANSFORMATIONS, 
  NOPERCENT_UNITS
} from "../../../types/constants";
import SatoriColorPicker from "../shared/satori-ui/colorpicker";
import contentHistoryManager from "../../../stores/content-history-manager";

const HEADING_LEVELS = [1, 2, 3, 4, 5, 6];

/**
 * HeadingStyles – Sidebar Editing Panel for Heading Blocks
 *
 * Provides a sidebar panel for configuring all typography and layout properties
 * of a Heading (H1–H6) content block in the Satori PageBuilder. Supports heading level selection,
 * color, font family, font size, weight, line height, letter spacing, text decoration,
 * text transform, and alignment.
 *
 * @component
 * @category OptionsPanel
 *
 * @param {Object} props - Component props.
 * @param {string} props.blockId - The unique ID of the Heading content block to edit.
 *
 * @example
 * // Render heading styles panel for a given Heading block
 * <HeadingStyles blockId={blockId} />
 *
 * @remarks
 * - Fetches and updates the target Heading block's data from the MobX contentManager store.
 * - Allows interactive heading level (H1–H6) selection, and applies the corresponding default styles.
 * - Renders grouped UI controls for all editable Heading fields, including advanced typography controls.
 * - Dimension fields (font size, line height, letter spacing) support px, rem, and em units with conversion.
 * - All updates call `contentManager.setHeadingConfig` to ensure atomic and reactive MobX state updates.
 * - Mirrors the options panel structure of ButtonOptions and ColumnsOptions for consistency.
 * - Fully reactive via MobX's `observer()` wrapper.
 *
 * @see contentManager.setHeadingConfig
 * @see bodyStylesManager – For default heading style sources
 * @see ButtonOptions, ColumnsOptions – For parallel sidebar editing patterns
 */
export default observer(function HeadingStyles({ blockId }: { blockId: string }) {
  const block = contentManager.getContentBlockById(blockId);
	console.log("blockId:", block?.contentBlockId);
	
  if (!block || block.blockType !== "heading") return null;

  const headingData = block.blockData as IHeadingBlockData;

  const parentFontSize = bodyStylesManager.getBodyStylesState().typography!["p"].fontSize;
  let parentRawFontSize: number = 0;  // We will always convert this into pixels first (if necessary)
  
  if (parentFontSize?.unit === "rem" || parentFontSize?.unit === "em")
    parentRawFontSize = parseFloat(parentFontSize!.value) * 16;
  else
    parentRawFontSize = parseInt(parentFontSize!.value);

  // Handler to update blockData
  function update(field: keyof IHeadingBlockData, value: any) {
    contentManager.setHeadingConfig(blockId, {
      ...headingData,
      [field]: value,
    });
  }

  // --- UI ---
  return (
    <section className="flex flex-col gap-3 p-4">
      {/* Heading Level Picker */}
      <div className="flex flex-col gap-2">
        <h4 className="font-semibold">Heading Level</h4>
        <div className="flex flex-row gap-2">
          {HEADING_LEVELS.map((level) => (
            <button
              key={level}
              className={`
                w-8 h-8 flex items-center justify-center border rounded-md
                text-sm font-semibold transition
                ${
                  headingData.headingLevel === level
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:border-blue-400"
                }
              `}
              onClick={() => {
                update("headingLevel", level);

                // We will pull the default heading styles from bodyStylesState
                const headingLevel = `h${level}`;
                const headingDefaults =
                  bodyStylesManager.getBodyStylesState().typography![
                    headingLevel as keyof ITypographyRole
                  ];

                // Next, we need to pass in the defaults
                update("color", headingDefaults.color || "#242424");
                update(
                  "fontFamily",
                  headingDefaults.fontFamily || "Inter, sans-serif"
                );
                update(
                  "fontSize",
                  headingDefaults.fontSize || { value: "2", unit: "rem" }
                );
                update("fontWeight", headingDefaults.fontWeight || "700");
                update(
                  "lineHeight",
                  headingDefaults?.lineHeight || { value: "1.2", unit: "rem" }
                );
                update(
                  "letterSpacing",
                  headingDefaults?.letterSpacing || { value: "0", unit: "rem" }
                );
                update(
                  "textDecoration",
                  headingDefaults?.textDecoration || "none"
                );
                update(
                  "textTransform",
                  headingDefaults?.textTransform || "none"
                );
                update("alignment", headingDefaults?.alignment || "left");

                glideBarManager.setCurrentHeading(headingLevel.toUpperCase());
              }}
              aria-label={`Heading ${level}`}
              type="button"
            >
              H{level}
            </button>
          ))}
        </div>
      </div>

      {/* Text Color */}
      <SatoriColorPicker
        label={"Text"}
        value={headingData.color || "#222222"}
        onChange={(val) => update("color", val)}
      />

      {/* Font Family */}
      <SatoriDropDownList
        label={"Font Family"}
        value={headingData.fontFamily || "Inter, sans-serif"}
        isFontFamily={true}
        onChange={(val) => {
          contentManager.setHeadingConfig(blockId, {
            ...headingData,
            fontFamily: val,
          });
        }}
      />

      {/* Font Size */}
      <SatoriDimensionsBox
        label={"Font Size"}
        value={cleanDimenValues(headingData.fontSize?.value!) || "2"}
        unit={headingData.fontSize?.unit || "rem"}
        units={NOPERCENT_UNITS}
        onValueChange={(val) =>
          update("fontSize", {
            value: val,
            unit: headingData.fontSize?.unit || "rem",
          })
        }
        onUnitChange={(unitVal) => {
          if (unitVal === "px") {
            // We need to change from rem/em value to pixel value
            update("fontSize", {
              value:
                Math.round(
                  parseFloat(headingData?.fontSize?.value!) * parentRawFontSize
                ).toString() || "",
              unit: unitVal,
            });
          } else if (unitVal === "rem" || unitVal === "em") {
            // We need to change from pixel value to rem/em value
            update("fontSize", {
              value:
                (
                  parseFloat(headingData?.fontSize?.value!) / parentRawFontSize
                ).toString() || "",
              unit: unitVal,
            });
          }
        }}
      />

      {/* Font Weight */}
      <SatoriDropDownList
        layout="horizontal"
        label={"Font Weight"}
        options={FONT_WEIGHTS}
        isFontFamily={false}
        value={headingData.fontWeight || "500"}
        onChange={(val) =>
          contentManager.setHeadingConfig(blockId, {
            ...headingData,
            fontWeight: val,
          })
        }
      />

      {/* Line Height */}
      <SatoriDimensionsBox
        label={"Line Height"}
        value={cleanDimenValues(headingData.lineHeight?.value!) || "1.2"}
        unit={headingData.lineHeight?.unit || ""}
        units={NOPERCENT_UNITS}
        onValueChange={(val) =>
          update("lineHeight", {
            value: val,
            unit: headingData.lineHeight?.unit || "",
          })
        }
        onUnitChange={(unitVal) => {
          if (unitVal === "px") {
            // We need to change from rem/em value to pixel value
            update("lineHeight", {
              value:
                Math.round(
                  parseFloat(headingData?.lineHeight?.value!) *
                    parentRawFontSize
                ).toString() || "",
              unit: unitVal,
            });
          } else if (unitVal === "rem" || unitVal === "em") {
            // We need to change from pixel value to rem/em value
            update("lineHeight", {
              value:
                (
                  parseFloat(headingData?.lineHeight?.value!) /
                  parentRawFontSize
                ).toString() || "",
              unit: unitVal,
            });
          }
        }}
      />

      {/* Letter Spacing */}
      <SatoriDimensionsBox
        label={"Letter Spacing"}
        value={cleanDimenValues(headingData.letterSpacing?.value!) || "1.2"}
        unit={headingData.letterSpacing?.unit || ""}
        units={NOPERCENT_UNITS}
        onValueChange={(val) =>
          update("letterSpacing", {
            value: val,
            unit: headingData.letterSpacing?.unit || "",
          })
        }
        onUnitChange={(unitVal) => {
          if (unitVal === "px") {
            // We need to change from rem/em value to pixel value
            update("letterSpacing", {
              value:
                Math.round(
                  parseFloat(headingData?.letterSpacing?.value!) *
                    parentRawFontSize
                ).toString() || "",
              unit: unitVal,
            });
          } else if (unitVal === "rem" || unitVal === "em") {
            // We need to change from pixel value to rem/em value
            update("letterSpacing", {
              value:
                (
                  parseFloat(headingData?.letterSpacing?.value!) /
                  parentRawFontSize
                ).toString() || "",
              unit: unitVal,
            });
          }
        }}
      />

      {/* Text Decoration */}
      <SatoriDropDownList
        layout="horizontal"
        label={"Text Decoration"}
        options={TEXT_DECORATIONS}
        isFontFamily={false}
        value={headingData.textDecoration || "none"}
        onChange={(val) => update("textDecoration", val)}
      />

      {/* Text Transform */}
      <SatoriDropDownList
        layout="horizontal"
        label={"Text Transform"}
        options={TEXT_TRANSFORMATIONS}
        isFontFamily={false}
        value={headingData.textTransform || "none"}
        onChange={(val) => update("textTransform", val)}
      />

      {/* Alignment */}
      <SatoriDropDownList
        layout="horizontal"
        label={"Alignment"}
        options={ALIGN_OPTIONS}
        isFontFamily={false}
        value={headingData.alignment || "none"}
        onChange={(val) => update("alignment", val)}
      />
    </section>
  );
});
